/**
 * CSRF Token 管理模块
 * @description 提供 CSRF Token 获取与请求拦截器集成功能
 * @module src/utils/v2/security/csrf
 * @since 2026-01-08
 * @see NFR-SEC-01 安全审计报告 P1-3
 */

/**
 * CSRF Token Cookie 名称
 * @description 后端设置的 Cookie 名称，通常遵循 Spring Security / Django 等框架默认值
 */
const CSRF_COOKIE_NAME = 'XSRF-TOKEN'

/**
 * CSRF Token Meta 标签名称
 * @description 备选方案：从页面 meta 标签读取 Token
 */
const CSRF_META_NAME = 'csrf-token'

/**
 * 需要携带 CSRF Token 的 HTTP 方法
 * @description 仅非幂等操作需要 CSRF 防护
 */
const CSRF_PROTECTED_METHODS = ['POST', 'PUT', 'DELETE', 'PATCH']

/**
 * 从 Cookie 中获取 CSRF Token
 * @returns CSRF Token 字符串，若不存在则返回 null
 */
function getTokenFromCookie(): string | null {
  if (typeof document === 'undefined') {
    return null
  }

  const cookies = document.cookie.split('; ')
  for (const cookie of cookies) {
    const [name, value] = cookie.split('=')
    if (name === CSRF_COOKIE_NAME && value) {
      // URI 解码，因为 Cookie 值可能被编码
      try {
        return decodeURIComponent(value)
      }
      catch {
        return value
      }
    }
  }

  return null
}

/**
 * 从 Meta 标签中获取 CSRF Token
 * @description 备选方案：适用于服务端渲染场景
 * @returns CSRF Token 字符串，若不存在则返回 null
 */
function getTokenFromMeta(): string | null {
  if (typeof document === 'undefined') {
    return null
  }

  const metaTag = document.querySelector(`meta[name="${CSRF_META_NAME}"]`)
  if (metaTag) {
    return metaTag.getAttribute('content')
  }

  return null
}

/**
 * 获取 CSRF Token
 * @description 优先从 Cookie 获取，若不存在则尝试从 Meta 标签获取
 * @returns CSRF Token 字符串，若均不存在则返回 null
 *
 * @example
 * ```ts
 * const csrfToken = getCSRFToken()
 * if (csrfToken) {
 *   config.headers['X-CSRF-Token'] = csrfToken
 * }
 * ```
 */
export function getCSRFToken(): string | null {
  // 优先从 Cookie 获取
  const cookieToken = getTokenFromCookie()
  if (cookieToken) {
    return cookieToken
  }

  // 备选：从 Meta 标签获取
  return getTokenFromMeta()
}

/**
 * 判断是否需要为该请求方法添加 CSRF Token
 * @param method - HTTP 请求方法（GET, POST, PUT, DELETE 等）
 * @returns 是否为需要 CSRF 防护的敏感方法
 *
 * @example
 * ```ts
 * if (shouldIncludeCSRFToken(config.method)) {
 *   const csrfToken = getCSRFToken()
 *   if (csrfToken) {
 *     config.headers['X-CSRF-Token'] = csrfToken
 *   }
 * }
 * ```
 */
export function shouldIncludeCSRFToken(method: string | undefined): boolean {
  if (!method) {
    return false
  }

  return CSRF_PROTECTED_METHODS.includes(method.toUpperCase())
}

/**
 * 获取 CSRF Token 请求头名称
 * @description 用于测试和配置场景
 */
export const CSRF_HEADER_NAME = 'X-CSRF-Token'

/**
 * 导出常量供测试使用
 */
export const CSRF_CONSTANTS = {
  COOKIE_NAME: CSRF_COOKIE_NAME,
  META_NAME: CSRF_META_NAME,
  HEADER_NAME: CSRF_HEADER_NAME,
  PROTECTED_METHODS: CSRF_PROTECTED_METHODS,
} as const
